import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Store, Factory, Paintbrush, Printer, PenTool, Calculator, Layers, Globe, ArrowRight, CheckCircle2 } from "lucide-react";

const ICONS = { store: Store, factory: Factory, paintbrush: Paintbrush, printer: Printer, "pen-tool": PenTool, calculator: Calculator, layers: Layers, globe: Globe };

export const SOLUTIONS_DEFAULT_DATA = {
  hero: {
    title_prefix: "Solutions for every",
    title_accent: "role",
    tagline: "Whether you're a distributor, supplier, decorator, print shop, sign company, or a QuickBooks user, Antera has purpose-built tools to simplify your operations.",
  },
  solutions: [
    {
      id: "distributors", icon: "store", title: "Distributors",
      tagline: "Manage stores, clients, approvals, and fulfillment with confidence at every stage.",
      image: "/api/media/0cbac729-0c0a-406d-8973-8e5c8466cee7", link: "/solutions/distributors",
      features: ["Client & contact management","Online store creation & management","Order entry and approval workflows","Fulfillment and shipping tracking","Commission tracking","Reporting and analytics"],
    },
    {
      id: "suppliers", icon: "factory", title: "Suppliers",
      tagline: "Scale with production management, inventory management, and PromoStandards endpoints.",
      image: "/api/media/ff0806c3-0718-4778-a0dd-53c0c702a9f2", link: "/solutions/suppliers",
      features: ["Production management workflows","Inventory management & tracking","PromoStandards data endpoints","Purchase order management","Vendor portal access","Real-time inventory sync"],
    },
    {
      id: "decorators", icon: "paintbrush", title: "Decorators",
      tagline: "Equip your staff with workflows and automation to deliver high-quality results on time.",
      image: "/api/media/d0a09e26-0562-4090-9567-7bed3fe4fb50", link: "/solutions/decorators",
      features: ["Art proof and approval workflows","Production scheduling","Job tracking and status updates","Multi-location management","Equipment & resource scheduling","Quality control tracking"],
    },
    {
      id: "print-shops", icon: "printer", title: "Print Shops",
      tagline: "Customizable flows to manage unlimited decoration types and keep jobs moving.",
      image: "/api/media/6e647c24-f203-425b-9b40-1a1af8cfd20e", link: "/solutions/print-shops",
      features: ["Multi-decoration type support","Custom workflow builder","Job costing and pricing","Production calendar","Customer portal","Automated status notifications"],
    },
    {
      id: "sign-companies", icon: "pen-tool", title: "Sign Companies",
      tagline: "Antera Software's Advance platform provides a centralized, cloud-based hub specifically designed to handle the complex, multi-step production requirements of sign companies. By unifying sales, design, and manufacturing into a single system, sign shops can eliminate disjointed processes and focus on high-margin growth.",
      image: "/api/media/18ad7bab-aedd-49bc-a0be-86afc82056d0", link: "/solutions/sign-companies",
      features: ["Specialized Production & Workflow Control","Streamlined Artwork and Brand Management","Integrated Business Operations","Strategic Data and Scalability","Industry-Expert Implementation and Support"],
    },
    {
      id: "quickbooks", icon: "calculator", title: "QuickBooks Users",
      tagline: "Antera Software provides a seamless, real-time integration with QuickBooks Online, acting as a specialized business management hub that fills critical gaps in standard accounting software for the promo, print, signage, and service industries.",
      image: "/api/media/8eaacdd2-95a5-41b0-be34-95a64e5f75ee", link: "/solutions/quickbooks",
      features: ["Elimination of Duplicate Data Entry","Industry-Specific Operational Gaps","Streamlined Financial Workflows","Strategic Financial Insights","Scalability for Growing Teams"],
    },
  ],
  bottom_cta: {
    heading: "Find the right solution for your business",
    subheading: "Schedule a personalized demo and our team will show you exactly how Antera can streamline your specific workflows.",
    primary_label: "Request a Demo",
    primary_href: "/contact",
    secondary_label: "Contact Sales",
    secondary_href: "/contact",
  },
};

export default function SolutionsPage({ data }) {
  const d = { ...SOLUTIONS_DEFAULT_DATA, ...(data || {}) };
  const hero = { ...SOLUTIONS_DEFAULT_DATA.hero, ...(d.hero || {}) };
  const bottom = { ...SOLUTIONS_DEFAULT_DATA.bottom_cta, ...(d.bottom_cta || {}) };
  const solutions = d.solutions || [];

  return (
    <div data-testid="solutions-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {hero.title_prefix} <span className="text-sky-600">{hero.title_accent}</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">{hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {solutions.map((s, i) => {
        const Icon = ICONS[s.icon] || Layers;
        return (
          <section key={s.id || i} id={s.id} data-testid={`solution-section-${s.id}`} className={`py-16 lg:py-24 ${i % 2 === 0 ? "bg-slate-50/50" : "bg-white"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{s.title}</h2>
                  <p className="mt-3 text-base text-slate-500 leading-relaxed">{s.tagline}</p>
                  <ul className="mt-6 space-y-3">
                    {(s.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link to="/contact">
                      <Button data-testid={`solution-${s.id}-demo-btn`} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 text-sm font-semibold">
                        Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    {s.link && (
                      <Link to={s.link}>
                        <Button variant="outline" data-testid={`solution-${s.id}-learn-more-btn`} className="border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-700 rounded-full px-6 text-sm font-semibold">
                          Learn More <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                <div className={`${i % 2 === 1 ? "lg:order-1" : ""} rounded-xl overflow-hidden shadow-lg border border-slate-200`}>
                  {s.image ? (
                    <img src={s.image} alt={s.title} className="w-full h-64 lg:h-80 object-cover" />
                  ) : (
                    <div className="bg-slate-100 h-64 lg:h-80 flex items-center justify-center">
                      <Icon className="h-20 w-20 text-slate-300" />
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{bottom.heading}</h2>
          <p className="mt-3 text-sm text-sky-100 max-w-lg mx-auto">{bottom.subheading}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={bottom.primary_href || "/contact"}>
              <Button data-testid="solutions-bottom-demo-btn" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
                {bottom.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            {bottom.secondary_label ? (
              <Link to={bottom.secondary_href || "/contact"}>
                <Button variant="outline" data-testid="solutions-bottom-contact-btn" className="border-white/40 text-white hover:bg-sky-700 rounded-full px-8 h-12 text-sm font-semibold">
                  {bottom.secondary_label}
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
